import type { Build } from '../../lib/trajectory/types';
import { useBowProfiles, useArrowProfiles } from '../../profiles/hooks';
import { buildFromProfiles } from '../../profiles/buildBridge';

interface BuildPickerProps {
  label: string;
  color: 'ember' | 'cyan';
  build: Build;
  onChange: (next: Build) => void;
}

/** Build fields edited by the numeric inputs below — all hold numbers. */
type NumericBuildField =
  | 'arrowGrains'
  | 'fOCPercent'
  | 'pointGrains'
  | 'bowIboFps'
  | 'bowDrawWeightLb'
  | 'bowDrawLengthIn';

export function BuildPicker({ label, color, build, onChange }: BuildPickerProps) {
  const { profiles: bowProfiles } = useBowProfiles();
  const { profiles: arrowProfiles } = useArrowProfiles();

  /* Numeric inputs share one guarded handler: a blank or non-numeric field
   * (common on mobile while editing) is ignored rather than coerced to 0.
   * arrowGrains and bowIboFps are divisors/speed sources where 0 yields a
   * NaN trajectory, so they additionally require a positive value. */
  function setNumericField(field: NumericBuildField, raw: string): void {
    const n = Number(raw);
    if (!Number.isFinite(n)) return;
    const mustBePositive = field === 'arrowGrains' || field === 'bowIboFps';
    if (mustBePositive && n <= 0) return;
    onChange({ ...build, [field]: n });
  }

  function loadFromSaved(bowId: string, arrowId: string) {
    const bow = bowProfiles.find((p) => p.profileId === bowId);
    const arrow = arrowProfiles.find((p) => p.profileId === arrowId);
    if (bow && arrow) {
      onChange(buildFromProfiles(bow, arrow));
    } else if (bow) {
      /* Just apply the bow specs, keep arrow as-is */
      onChange({
        ...build,
        bowIboFps: bow.bow.iboFps,
        bowDrawWeightLb: bow.bow.drawWeightLb,
        bowDrawLengthIn: bow.bow.drawLengthIn,
        measuredVelocityFps: bow.bow.measuredVelocityFps,
      });
    }
  }

  return (
    <div className={`build-picker build-picker-${color}`}>
      <h3>{label}</h3>
      {(bowProfiles.length > 0 || arrowProfiles.length > 0) && (
        <div className="picker-load">
          {bowProfiles.length > 0 && (
            <select
              className="load-saved"
              defaultValue=""
              onChange={(e) => {
                const bowId = e.target.value;
                if (bowId) loadFromSaved(bowId, '');
                e.target.value = '';
              }}
              title="Load specs from a saved bow profile"
            >
              <option value="">Load bow profile…</option>
              {bowProfiles.map((p) => (
                <option key={p.profileId} value={p.profileId}>
                  {p.name} {p.isPrimary ? '(primary)' : ''}
                </option>
              ))}
            </select>
          )}
          {arrowProfiles.length > 0 && bowProfiles.length > 0 && (
            <select
              className="load-saved"
              defaultValue=""
              onChange={(e) => {
                const arrowId = e.target.value;
                const primary = bowProfiles.find((p) => p.isPrimary) ?? bowProfiles[0];
                if (arrowId && primary) loadFromSaved(primary.profileId, arrowId);
                e.target.value = '';
              }}
              title="Load specs from a saved arrow profile (uses primary bow)"
            >
              <option value="">Load arrow profile…</option>
              {arrowProfiles.map((p) => (
                <option key={p.profileId} value={p.profileId}>
                  {p.name} {p.isActive ? '(active)' : ''}
                </option>
              ))}
            </select>
          )}
        </div>
      )}
      <div className="picker-row">
        <label>Arrow grains</label>
        <input
          type="number"
          value={build.arrowGrains}
          step={10}
          min={300}
          max={800}
          onChange={(e) => setNumericField('arrowGrains', e.target.value)}
        />
      </div>
      <div className="picker-row">
        <label>FOC %</label>
        <input
          type="number"
          value={build.fOCPercent}
          step={0.5}
          min={5}
          max={25}
          onChange={(e) => setNumericField('fOCPercent', e.target.value)}
        />
      </div>
      <div className="picker-row">
        <label>Point grains</label>
        <input
          type="number"
          value={build.pointGrains}
          step={25}
          min={75}
          max={300}
          onChange={(e) => setNumericField('pointGrains', e.target.value)}
        />
      </div>
      <div className="picker-row">
        <label>Point type</label>
        <select
          value={build.pointType}
          onChange={(e) => onChange({ ...build, pointType: e.target.value as Build['pointType'] })}
        >
          <option value="COC fixed">COC fixed</option>
          <option value="single-bevel">Single-bevel</option>
          <option value="fixed mech">Fixed mech</option>
          <option value="hybrid mech">Hybrid mech</option>
          <option value="full mech">Full mech</option>
        </select>
      </div>
      <div className="picker-row">
        <label>Bow IBO fps</label>
        <input
          type="number"
          value={build.bowIboFps}
          step={1}
          min={280}
          max={360}
          onChange={(e) => setNumericField('bowIboFps', e.target.value)}
        />
      </div>
      <div className="picker-row">
        <label>Chrono fps <span style={{opacity:0.5}}>(optional)</span></label>
        <input
          type="number"
          value={build.measuredVelocityFps ?? ''}
          step={1}
          min={200}
          max={400}
          placeholder="auto"
          onChange={(e) => {
            const v = e.target.value === '' ? undefined : Number(e.target.value);
            onChange({ ...build, measuredVelocityFps: v });
          }}
        />
      </div>
      <div className="picker-row">
        <label>Draw weight</label>
        <input
          type="number"
          value={build.bowDrawWeightLb}
          step={5}
          min={40}
          max={90}
          onChange={(e) => setNumericField('bowDrawWeightLb', e.target.value)}
        />
      </div>
      <div className="picker-row">
        <label>Draw length</label>
        <input
          type="number"
          value={build.bowDrawLengthIn}
          step={0.25}
          min={24}
          max={32}
          onChange={(e) => setNumericField('bowDrawLengthIn', e.target.value)}
        />
      </div>
      <div className="picker-row">
        <label>Vane geometry</label>
        <select
          value={build.vaneGeometry}
          onChange={(e) => onChange({ ...build, vaneGeometry: e.target.value as Build['vaneGeometry'] })}
        >
          <option value="DCA Sabre">DCA Sabre</option>
          <option value="AAE Talon">AAE Talon</option>
          <option value="Aerovane 2.2">Aerovane 2.2</option>
          <option value="AAE Max Stealth">AAE Max Stealth</option>
          <option value="Bohning Blazer">Bohning Blazer</option>
          <option value="Other">Other</option>
        </select>
      </div>
      <div className="picker-row">
        <label>Tune quality (yaw °)</label>
        <input
          type="range"
          value={build.tuneQualityYawDeg}
          step={0.5}
          min={0}
          max={15}
          onChange={(e) => onChange({ ...build, tuneQualityYawDeg: Number(e.target.value) })}
        />
        <span className="tune-value">{build.tuneQualityYawDeg.toFixed(1)}°</span>
      </div>
    </div>
  );
}
